import {
  learnerGamesApi,
  type GameRankingEntry,
  type GameRankingResponse,
} from '@/api/learnerGames'
import type { SubmitGameScoreVars } from '@/hooks/learner/useSubmitGameScore'

export type PlaygroundApiOperation = 'score' | 'ranking' | 'loadState' | 'saveState'
type GameSub = string | null | undefined
type ScoreCallback = (entry: GameRankingEntry) => void

/** Legacy JS 게임에 넘기는 LC 호스트 콜백. API 식별자는 항상 route의 gameKey다. */
export interface PlaygroundGameApi {
  onGameCompleted(gameName: string, sub: GameSub, score: number, time: number, callback?: ScoreCallback): Promise<void>
  onScoreUpdate(gameName: string, sub: GameSub, score: number, time: number, callback?: ScoreCallback): Promise<void>
  loadRanking(gameName: string, sub: GameSub, callback: (data: GameRankingResponse) => void, count?: number): Promise<void>
  loadState(gameName: string, sub: GameSub, callback: (data: unknown) => void): Promise<void>
  saveState(gameName: string, sub: GameSub, data: unknown, callback?: (data: unknown) => void): Promise<void>
}

interface Options {
  gameKey: string
  submitScore: (request: SubmitGameScoreVars) => Promise<GameRankingEntry>
  onError: (operation: PlaygroundApiOperation, error: unknown) => void
}

function objectState(data: unknown): Record<string, unknown> {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new Error('Invalid game state payload')
  }
  return data as Record<string, unknown>
}

/** Farm sends remaining milliseconds; the existing LC score contract uses nonnegative seconds. */
function farmTimeSeconds(milliseconds: number): number {
  if (!Number.isFinite(milliseconds)) throw new Error('Invalid Farm remaining time')
  return Number((Math.max(0, milliseconds) / 1000).toFixed(1))
}

export function createPlaygroundGameApi({ gameKey, submitScore, onError }: Options): PlaygroundGameApi {
  const isRabbitFarm = gameKey === 'lostTemple'
  const run = async (operation: PlaygroundApiOperation, request: () => Promise<void>) => {
    try {
      await request()
    } catch (error) {
      // 실패 응답을 빈 상태나 제출 성공처럼 게임에 돌려주지 않는다.
      onError(operation, error)
    }
  }
  const complete = (sub: GameSub, score: number, time: number, callback?: ScoreCallback) =>
    run('score', async () => {
      const entry = await submitScore({ key: gameKey, score, time: isRabbitFarm ? farmTimeSeconds(time) : time, sub })
      if (!entry) throw new Error('Missing game score response')
      callback?.(entry)
    })

  return {
    onGameCompleted: (_gameName, sub, score, time, callback) => complete(sub, score, time, callback),
    onScoreUpdate: (_gameName, sub, score, time, callback) => {
      // Farm reports intermediate score/time=0. Persist only the final onComplete.
      // PenguinRun and other games retain their existing score-update submission.
      return isRabbitFarm ? Promise.resolve() : complete(sub, score, time, callback)
    },
    loadRanking: (_gameName, sub, callback, count) => run('ranking', async () => {
      const response = await learnerGamesApi.loadRanking(gameKey, { sub, count })
      if (response.data.success === false || !response.data.data) throw new Error('Missing game ranking response')
      callback(response.data.data)
    }),
    loadState: async (_gameName, sub, callback) => {
      if (!isRabbitFarm) {
        callback({})
        return
      }
      await run('loadState', async () => {
        const response = await learnerGamesApi.loadState(gameKey, { sub })
        if (response.data.success === false || !response.data.data) throw new Error('Missing game state response')
        callback(objectState(response.data.data.data))
      })
    },
    saveState: async (_gameName, sub, data, callback) => {
      if (!isRabbitFarm) {
        callback?.(data)
        return
      }
      await run('saveState', async () => {
        const response = await learnerGamesApi.saveState(gameKey, { data: objectState(data) }, { sub })
        if (response.data.success === false || !response.data.data) throw new Error('Missing game state response')
        callback?.(objectState(response.data.data.data))
      })
    },
  }
}
